import querystring from "query-string";
import axiosInstance, { errorHandler } from "./api";
import { imageEncode } from "../util/imageEncoder";
import { dateFormatter } from "@/util/formatter";

const VERIFICATION_URL_V2 = "/tps/api/v2/verification-requests";
const VERIFICATION_URL = "/tps/api/verification-requests";

// VerificationRequestResource.getAllMobileVerificationRequests
const getVerificationRequestsMobile = axios => (
  page,
  size,
  filter = "",
  sort = "",
) =>
  axios
    .get(
      `${VERIFICATION_URL_V2}/mobile?${querystring.stringify({
        page,
        size,
        filter,
        sort,
      })}`,
    )
    .then(({ data, headers }) => ({
      data,
      total: Number(headers["x-total-count"]),
    }))
    .catch(errorHandler);

// VerificationRequestResource.getAllDesktopVerificationRequests
const getVerificationRequestsDesktop = axios => (
  page,
  size,
  filter = "",
  sort = "",
) =>
  axios
    .get(
      `${VERIFICATION_URL_V2}/desktop?${querystring.stringify({
        page,
        size,
        filter,
        sort,
      })}`,
    )
    .then(({ data, headers }) => ({
      data,
      total: Number(headers["x-total-count"]),
    }))
    .catch(errorHandler);

// VerificationRequestResource.getAllDesktopVerificationRequests
const getVerificationRequestsWeb = axios => (
  page,
  size,
  filter = "",
  sort = "",
) =>
  axios
    .get(
      `${VERIFICATION_URL_V2}/web?${querystring.stringify({
        page,
        size,
        filter,
        sort,
      })}`,
    )
    .then(({ data, headers }) => ({
      data,
      total: Number(headers["x-total-count"]),
    }))
    .catch(errorHandler);

// VerificationRequestResource.updateVerificationRequest
const getVerificationRequest = axios => id =>
  axios
    .get(`${VERIFICATION_URL_V2}/${id}`)
    .then(({ data }) => data)
    .catch(errorHandler);

const getActiveVerificationRequest = axios => accountId =>
  axios
    .get(`${VERIFICATION_URL}/get-active-for-account/${accountId}`)
    .then(({ data }) => data)
    .catch(errorHandler);

// VerificationRequestResource.approveVerificationRequest
const approveVerificationRequest = axios => ({ verificationId, limitId }) =>
  axios
    .put(`${VERIFICATION_URL}/approve`, { verificationId, limitId })
    .then(() => {})
    .catch(errorHandler);

// VerificationRequestResource.rejectVerificationRequest
const rejectVerificationRequest = axios => (verificationId, rejectedReason) =>
  axios
    .put(`${VERIFICATION_URL}/reject`, { verificationId, rejectedReason })
    .then(() => {})
    .catch(errorHandler);

const getDocumentsPhoto = axios => (identityUuid = "") =>
  axios
    .get(`/authservice/api/v1/user/identity?uuid=${identityUuid}`, {
      headers: {
        Accept: "image/*",
      },
      responseType: "arraybuffer",
    })
    .then(res => imageEncode(res.data, res.headers["content-type"]))
    .catch(errorHandler);

const getUserPhoto = axios => (photoUuid = "") =>
  axios
    .get(`/authservice/api/v1/user/photo?uuid=${photoUuid}`, {
      headers: {
        Accept: "image/*",
      },
      responseType: "arraybuffer",
    })
    .then(res => imageEncode(res.data, res.headers["content-type"]))
    .catch(errorHandler);

// Returns a map of short-lived pre-signed MinIO download links for the
// account's KYC artifacts, e.g. { liveNess: "https://.../presigned" }.
const getKycDownloadLinks = axios => (accountId = "") =>
  axios
    .get(
      `/authservice/api/v2/user/${encodeURIComponent(
        accountId,
      )}/kyc-download-links`,
    )
    .then(({ data }) => data.data)
    .catch(errorHandler);

// FstIrohaAccountResource.verifyPassport
// Verifies the mobile user's passport with GDI (via OB Shared KYC).
// Resolves to { status: "APPROVED" | "IN_REVIEW" }.
const verifyPassportWithGdi = axios => (accountId = "") =>
  axios
    .post(
      `/tps/api/v2/fst-iroha-accounts/${encodeURIComponent(
        accountId,
      )}/verify-passport`,
    )
    .then(({ data }) => data.data)
    .catch(errorHandler);

/**
 * Fetches a list of updates of user's account
 */
const getUserHistoryByAccountId = axios => (accountId = "") =>
  axios
    .get(`/authservice/api/v1/user/edit-profile/history/${accountId}`)
    .then(({ data }) =>
      data.map(r => ({ ...r, dateTime: dateFormatter(r.dateTime) })),
    )
    .catch(errorHandler);

/**
 * Update user account
 */
const updateUserAccount = axios => form =>
  axios
    .post("/authservice/api/v1/user/edit-profile", form)
    .then(({ data }) => data)
    .catch(errorHandler);

const getUserIdentityHistory = axios => accountId =>
  axios
    .get(`/authservice/api/v1/user/edit-profile/identity/history/${accountId}`)
    .then(({ data }) =>
      data.map(r => ({
        ...r,
        dateTime: dateFormatter(r.dateTime),
        live: String(r.live),
        faceMatch: String(r.faceMatch),
      })),
    )
    .catch(errorHandler);

const updateAdditionalInfo = axios => form =>
  axios
    .post("/authservice/api/v1/user/update-additional-info", form)
    .then(({ data }) => data)
    .catch(errorHandler);

export default {
  getVerificationRequest: getVerificationRequest(axiosInstance),
  approveVerificationRequest: approveVerificationRequest(axiosInstance),
  rejectVerificationRequest: rejectVerificationRequest(axiosInstance),
  getActiveVerificationRequest: getActiveVerificationRequest(axiosInstance),
  getDocumentsPhoto: getDocumentsPhoto(axiosInstance),
  getUserPhoto: getUserPhoto(axiosInstance),
  getKycDownloadLinks: getKycDownloadLinks(axiosInstance),
  verifyPassportWithGdi: verifyPassportWithGdi(axiosInstance),
  getVerificationRequestsMobile: getVerificationRequestsMobile(axiosInstance),
  getVerificationRequestsDesktop: getVerificationRequestsDesktop(axiosInstance),
  getVerificationRequestsWeb: getVerificationRequestsWeb(axiosInstance),
  getUserHistoryByAccountId: getUserHistoryByAccountId(axiosInstance),
  updateUserAccount: updateUserAccount(axiosInstance),
  getUserIdentityHistory: getUserIdentityHistory(axiosInstance),
  updateAdditionalInfo: updateAdditionalInfo(axiosInstance),
};
